import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, CanMatchFn, Route, UrlSegment } from '@angular/router';
import { isValidPartnerSlug, PartnerContextService } from '../services/partner-context.service';

/** Name of the route parameter carrying the partner slug, e.g. `/:partnerSlug/reports`. */
export const PARTNER_SLUG_PARAM = 'partnerSlug';

/**
 * Lets the partner routes match only an address whose first segment looks like a partner
 * slug. Anything else (a typo, an old link, a hand-written URL) falls through to the
 * "unknown partner address" page instead of rendering a panel for a nonsense partner.
 */
export const partnerSlugMatchGuard: CanMatchFn = (_route: Route, segments: UrlSegment[]) =>
  segments.length > 0 && isValidPartnerSlug(segments[0].path);

/**
 * Points the panel at the partner named in the URL before anything under it is activated —
 * in particular before `authGuard`, which decides whether the stored session belongs to
 * THIS partner.
 *
 * The router re-runs it whenever the slug segment changes (default `paramsChange`), so
 * moving from one partner's address to another switches the panel over.
 */
export const partnerSlugGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const slug = route.paramMap.get(PARTNER_SLUG_PARAM);

  // Unreachable through the router (canMatch already validated the segment), but a guard
  // that silently accepted an invalid slug would let it into the panel's state.
  if (!slug || !isValidPartnerSlug(slug)) {
    return false;
  }

  inject(PartnerContextService).applySlug(slug);

  return true;
};
